import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Printer, FileText, Upload, ImageIcon, Loader2, Info, UserPlus } from "lucide-react";
import api, { API, getToken, formatApiError } from "@/lib/api";
import { StatusBadge, StatusTracker } from "@/components/OrderBits";
import { toast } from "sonner";

export default function OrderDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const g = params.get("g");
  const [order, setOrder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const qs = g ? `?g=${g}` : "";
  const load = () => api.get(`/orders/${id}${qs}`).then((r) => setOrder(r.data)).catch(() => setOrder(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/orders/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (order === null) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;
  if (order === false) return <div className="mx-auto max-w-3xl px-6 py-20 text-center text-slate-500">Order not found. <Link to="/dashboard" className="text-teal">Back to dashboard</Link></div>;

  const token = getToken();
  const isGuest = !!g;
  const authQs = isGuest ? `g=${g}` : `auth=${token}`;
  const labelUrl = `${API}/orders/${id}/label?${authQs}`;
  const reportUrl = `${API}/orders/${id}/report?${authQs}`;

  return (
    <div className="bg-background" data-testid="order-detail-page">
      <div className="mx-auto max-w-4xl px-6 py-12 md:px-10 md:py-16">
        {isGuest ? (
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-ocean"><ArrowLeft className="h-4 w-4" /> Back to home</Link>
        ) : (
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-ocean"><ArrowLeft className="h-4 w-4" /> Back to dashboard</Link>
        )}

        <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-ocean">Order #{order.id.slice(0, 8).toUpperCase()}</h1>
            <p className="mt-1 text-slate-500">Placed {new Date(order.created_at).toLocaleString()}</p>
          </div>
          <StatusBadge status={order.status} />
        </div>

        {/* Tracker */}
        <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-white p-6">
          <StatusTracker status={order.status} />
        </div>

        {/* Actions */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <a href={labelUrl} target="_blank" rel="noreferrer" data-testid="order-print-label"
            className="flex items-center gap-3 rounded-2xl border border-teal bg-accent/40 p-6 transition-colors hover:bg-accent">
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-teal text-white"><Printer className="h-6 w-6" /></span>
            <div><p className="font-semibold text-ocean">Print shipping label</p><p className="text-sm text-slate-500">Prepaid label — print &amp; ship your sample</p></div>
          </a>
          {order.report ? (
            <a href={reportUrl} target="_blank" rel="noreferrer" data-testid="order-download-report"
              className="flex items-center gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-6 transition-colors hover:bg-emerald-100">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-500 text-white"><FileText className="h-6 w-6" /></span>
              <div><p className="font-semibold text-ocean">Download your report</p><p className="text-sm text-slate-500">Your results are ready</p></div>
            </a>
          ) : (
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-white p-6 opacity-70">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-slate-100 text-slate-400"><FileText className="h-6 w-6" /></span>
              <div><p className="font-semibold text-slate-500">Report pending</p><p className="text-sm text-slate-400">Available once testing is complete</p></div>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="mt-6 rounded-2xl border border-border bg-white p-6">
          <h3 className="font-semibold text-ocean">Tests in this order</h3>
          <div className="mt-4 divide-y divide-border">
            {order.items.map((it, i) => (
              <div key={i} className="flex items-center justify-between py-3">
                <div><p className="font-medium text-slate-700">{it.tier_name}{it.supplement_name ? ` — ${it.supplement_name}` : ""}</p><p className="text-xs text-slate-500">{it.included || ""}</p></div>
                <span className="text-sm font-semibold text-ocean">${it.price?.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1 border-t border-border pt-4 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>${order.subtotal?.toFixed(2)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-teal-dark"><span>Discount</span><span>−${order.discount?.toFixed(2)}</span></div>}
            {order.rush_fee > 0 && <div className="flex justify-between text-slate-600"><span>Rush</span><span>${order.rush_fee?.toFixed(2)}</span></div>}
            <div className="flex justify-between pt-1 font-bold text-ocean"><span>Total</span><span>${order.total?.toFixed(2)}</span></div>
          </div>
        </div>

        {/* Photos */}
        {!isGuest && (
        <div className="mt-6 rounded-2xl border border-border bg-white p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-ocean">Supplement photos</h3>
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="order-upload-photo"
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold text-ocean hover:bg-secondary disabled:opacity-60">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
          </div>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500"><Info className="h-3.5 w-3.5" /> Optional — helps us match your sample. Photos of the label and bottle are useful.</p>
          {order.photos?.length > 0 ? (
            <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
              {order.photos.map((p) => (
                <a key={p.id} href={`${API}/files/${p.storage_path}?auth=${token}`} target="_blank" rel="noreferrer" className="group block overflow-hidden rounded-xl border border-border">
                  <img src={`${API}/files/${p.storage_path}?auth=${token}`} alt={p.filename} className="h-28 w-full object-cover transition-transform group-hover:scale-105" />
                </a>
              ))}
            </div>
          ) : (
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-dashed border-border p-6 text-sm text-slate-400"><ImageIcon className="h-5 w-5" /> No photos uploaded yet.</div>
          )}
        </div>
        )}

        {/* Guest: prompt to create an account */}
        {isGuest && (
          <div className="mt-6 flex flex-col items-start gap-3 rounded-2xl border border-teal/40 bg-accent/40 p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="guest-account-cta">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-teal text-white"><UserPlus className="h-5 w-5" /></span>
              <div>
                <p className="font-semibold text-ocean">Keep all your results in one place</p>
                <p className="text-sm text-slate-600">Bookmark this page to print your label or download results anytime. Create a free account to upload supplement photos and track every test.</p>
              </div>
            </div>
            <Link to="/register" className="shrink-0 rounded-full bg-ocean px-5 py-2.5 text-sm font-semibold text-white hover:bg-ocean-light">Create account</Link>
          </div>
        )}

        {/* History */}
        {order.status_history?.length > 0 && (
          <div className="mt-6 rounded-2xl border border-border bg-white p-6">
            <h3 className="font-semibold text-ocean">Status history</h3>
            <div className="mt-4 space-y-3">
              {order.status_history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="h-2 w-2 rounded-full bg-teal" />
                  <span className="font-medium text-slate-700">{h.status}</span>
                  <span className="text-slate-400">{new Date(h.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
